import React, { FC, memo, ReactNode } from 'react';
import Grid from '@mui/material/Grid';
import { FieldArray } from 'formik';
import Button from '@mui/material/Button';
import get from 'lodash/get';
import omit from 'lodash/omit';
import IconButton from '@mui/material/IconButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import cloneDeep from 'lodash/cloneDeep';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import makeStyles from '@mui/styles/makeStyles';

import { Theme } from '../../types';
import { Box, Divider, Tooltip, Typography } from '@mui/material';

const useStyles = makeStyles((theme: Theme) => ({
    gridGap0_1: {
        gridGap: theme.spacing(0, 1)
    },
    paddingBottom2: {
        paddingBottom: theme.spacing(2)
    },
    margin0: {
        margin: 0
    },
    alignTop: {
        alignSelf: 'flex-start'
    },
    marginBottom1: {
        marginBottom: theme.spacing(1)
    }
}));

export type FormFieldArrayRowFields = {
    index: number;
    fieldName: string;
    propsToRowFields?: any & { getExtraRowAmountLabel?: (amount: number) => string };
};

interface Props<T> {
    fieldName: string;
    getEmptyRow: (startDate: string, endDate: string) => Partial<T | null>;
    rowFields: FC<FormFieldArrayRowFields>;
    propsToRowFields?: any;
    label?: string;
    withCreatedAt?: boolean;
    gridClassName?: string;
    disabled?: boolean;
    divider?: boolean;
    defaultFirstRow?: boolean;
    numbering?: boolean;
    initialValues?: any;
    /** Wrap each row in a collapsible Accordion. First row auto-expanded; others collapsed when total rows exceed expandFirstN. */
    collapsible?: boolean;
    /** When collapsible, this many rows from the top stay expanded by default. Default 1. */
    expandFirstN?: number;
    /** When collapsible, returns the header content for each row. */
    getRowSummary?: (row: any, index: number) => ReactNode;
}

const FormFieldArray = <T extends object>(props: Props<T>) => {
    const {
        fieldName,
        getEmptyRow,
        rowFields: RowFields,
        propsToRowFields = {},
        label = 'Item',
        withCreatedAt = false,
        gridClassName = '',
        disabled = false,
        divider = false,
        defaultFirstRow = false,
        numbering = false,
        initialValues,
        collapsible = false,
        expandFirstN = 1,
        getRowSummary
    } = props;
    const classes = useStyles();

    return (
        <FieldArray
            name={fieldName}
            render={(arrayHelpers) => {
                if (defaultFirstRow && !arrayHelpers.form.values[fieldName]?.length) {
                    const { startDate, endDate } = arrayHelpers.form.initialValues; // Destructure the startDate and endDate from initialValues
                    arrayHelpers.form.values[fieldName] = [getEmptyRow(startDate, endDate)];
                }
                return (
                    <>
                        {(() => {
                    const rows = get(arrayHelpers.form.values, fieldName, []) || [];
                    const useCollapsible = collapsible && rows.length > expandFirstN;

                    const renderButtons = (rowFields: any, index: number, asEdge = false) => (
                        <>
                            <Tooltip title="Duplicate">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (disabled) return;
                                        const duplicateRow = cloneDeep(omit(rowFields, '_id'));
                                        if (withCreatedAt) duplicateRow.createdAt = new Date();
                                        arrayHelpers.push(duplicateRow);
                                    }}
                                    className={classes.margin0}
                                    edge={asEdge ? 'end' : false}
                                    size="large"
                                >
                                    <FileCopyIcon color="secondary" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        !disabled && arrayHelpers.remove(index);
                                    }}
                                    className={classes.margin0}
                                    size="large"
                                >
                                    <DeleteForeverIcon color="secondary" />
                                </IconButton>
                            </Tooltip>
                        </>
                    );

                    return rows.map((rowFields: any, index: number) => {
                        if (useCollapsible) {
                            return (
                                <Accordion key={index} defaultExpanded={index < expandFirstN} disableGutters>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                            <Typography sx={{ flex: 1 }}>
                                                {getRowSummary
                                                    ? getRowSummary(rowFields, index)
                                                    : `${label} ${index + 1}`}
                                            </Typography>
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                {renderButtons(rowFields, index)}
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <RowFields
                                            index={index}
                                            fieldName={fieldName}
                                            propsToRowFields={propsToRowFields}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            );
                        }
                        return (
                            <Box key={index}>
                                <Grid
                                    className={`${classes.gridGap0_1} ${gridClassName} ${classes.paddingBottom2}`}
                                    container
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                >
                                    {numbering && (
                                        <Typography color="primary" variant="h5" className={classes.alignTop}>
                                            {index + 1}
                                        </Typography>
                                    )}
                                    <RowFields
                                        index={index}
                                        fieldName={fieldName}
                                        propsToRowFields={propsToRowFields}
                                    />
                                    <Box pt={2}>{renderButtons(rowFields, index, true)}</Box>
                                </Grid>
                                {divider && <Divider className={classes.marginBottom1} />}
                            </Box>
                        );
                    });
                })()}
                        <Grid container justifyContent={defaultFirstRow ? 'flex-end' : 'flex-start'} sx={{ mt: 1 }}>
                            <Button
                                type="button"
                                color="primary"
                                onClick={() => !disabled && arrayHelpers.push(getEmptyRow(initialValues?.startDate, initialValues?.endDate))}
                            >
                                + Add a new {label}
                            </Button>
                        </Grid>
                    </>
                );
            }}
        />
    );
};

export default memo(FormFieldArray);
